import type { PaymentMethod as StripePaymentMethod } from '@stripe/stripe-js';
import type { AvailablePaymentMethod, Customer, PaymentKeys, PaymentMethod } from '@/types/checkout';
import { PaymentMethodEnum, ProviderEnum, type ExcludeOtherPaymentMethod } from '@/types/common';

export const buildStripeGenericPaymentRequest = (
  stripePaymentMethod: StripePaymentMethod,
  paymentMethodSettings: PaymentMethod,
  customer: Customer
) => {
  return {
    paymentProvider: ProviderEnum.STRIPE,
    receipt_email: stripePaymentMethod?.billing_details.email ?? customer.email,
    params: {
      paymentMethodId: stripePaymentMethod?.id,
    },
    paymentMethodDisplayName: paymentMethodSettings.paymentMethodDisplayName,
    paymentMethodId: paymentMethodSettings.id,
  };
};

export const getStripePaymentKeyBySelectedPaymentMethod = (
  paymentMethods: PaymentMethod[],
  selectedPaymentMethod: ExcludeOtherPaymentMethod,
  paymentKeys: PaymentKeys
) => {
  const stripePaymentMethod = paymentMethods.find((pm) => pm.paymentMethodName === selectedPaymentMethod);
  if (!stripePaymentMethod) {
    return paymentKeys.stripe;
  }
  return stripePaymentMethod.pspSubMidPk;
};

export const getStripePaymentKey = (paymentKeys: PaymentKeys, paymentMethods: PaymentMethod[]) => {
  if (paymentKeys.stripe) {
    return paymentKeys.stripe;
  }

  const stripePaymentMethod = paymentMethods.find((pm) => pm.provider === ProviderEnum.STRIPE);

  return stripePaymentMethod?.pspSubMidPk;
};

export const getStripeErrorMessage = (
  t: (key: string) => string,
  error: { type: string; code?: string; message?: string } | null,
  fieldType: 'number' | 'expiry' | 'cvc'
): string | null => {
  if (!error) return null;

  const errorCode = (error as { code?: string }).code || error.type;

  switch (fieldType) {
    case 'number':
      if (errorCode === 'incomplete_number' || errorCode === 'incomplete') {
        return t('fieldErrors.incompleteCard');
      }
      if (errorCode === 'invalid_number' || errorCode === 'incorrect_number') {
        return t('fieldErrors.invalidCard');
      }
      return t('fieldErrors.invalidCard');
    case 'expiry':
      if (errorCode === 'incomplete_expiry' || errorCode === 'incomplete') {
        return t('fieldErrors.incompleteExpirationDate');
      }
      if (errorCode === 'invalid_expiry_year' || errorCode === 'expired_card') {
        return t('fieldErrors.invalidExpirationDate');
      }
      return t('fieldErrors.incompleteExpirationDate');
    case 'cvc':
      if (errorCode === 'incomplete_cvc' || errorCode === 'incomplete') {
        return t('fieldErrors.incompleteSecurityCode');
      }
      if (errorCode === 'invalid_cvc') {
        return t('fieldErrors.invalidSecurityCode');
      }
      return t('fieldErrors.incompleteSecurityCode');
    default:
      return null;
  }
};

export const isStripeExpressPaymentMethodExists = (
  paymentMethods: AvailablePaymentMethod[],
  paymentMethodName: PaymentMethodEnum
) => {
  return !!paymentMethods.find(
    (pm) => pm.paymentMethodName === paymentMethodName && pm.provider === ProviderEnum.STRIPE && pm.isExpress
  );
};
